/** An activity's opening hours (plan §3 "Opening hours widget", D5): one row per weekday,
 *  each Closed / Open 24 h / one or more `HH:MM–HH:MM` periods — over the stored
 *  `opening_hours`, Google's legacy `periods` list that the app reads:
 *  `[{open: {day, time: "HHMM", hours, minutes}, close: {…}}]`, day 0 = Sunday.
 *
 *  Reading is lenient, because dev holds every shape there is (sampled 2026-10-04): `[]`;
 *  Google's always-open form (one `open` at Sunday 00:00, no `close`); overnight periods
 *  whose `close` is the next day; the dashboard's `hide` flag (a hidden period is a closed
 *  day); stale `nextDate`s; and lists stored as a JSON string or a Python repr. What the
 *  week cannot show exactly is said in `notes`, never silently changed.
 *
 *  Writing is canonical (`toOpeningPeriods`): every period carries `day`, `time`, `hours`
 *  and `minutes` (the Google and dashboard shape; the backend importer reads
 *  `hours` / `minutes`); a close before its open is the next day; a 24-hour day is
 *  00:00–23:59 on that day (what the dashboard and the importer read as one day — Google's
 *  own "close the next day at 00:00" reads as two days there); seven 24-hour days are
 *  Google's always-open form. Unit A7 checks the app's schema before the backend writer is
 *  fixed (plan §3); this module is the one place to change if it disagrees. Pure. */

/** Google's day numbers: 0 = Sunday … 6 = Saturday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const DAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];

/** The order the rows read in: Monday first. */
export const WEEK_ORDER: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 0];

export const WEEKDAY_NAME: Readonly<Record<Weekday, string>> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export const WEEKDAY_SHORT: Readonly<Record<Weekday, string>> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

/** One opening, wall clock `HH:MM` (24-hour). A close before the open is the next day:
 *  "18:00–02:00", "18:00–00:00" (until midnight). */
export interface OpenPeriod {
  open: string;
  close: string;
}

export type DayKind = 'closed' | 'open24' | 'hours';

export type DayHours =
  | { kind: 'closed' }
  | { kind: 'open24' }
  | { kind: 'hours'; periods: OpenPeriod[] };

export type WeekHours = Readonly<Record<Weekday, DayHours>>;

/** A day's choice, worded as its state: "Open" is followed by its periods. */
export const DAY_KIND_LABEL: Readonly<Record<DayKind, string>> = {
  closed: 'Closed',
  hours: 'Open',
  open24: 'Open 24 h',
};

/** The stored shape (what the app reads). */
export interface OpeningPoint {
  day: number;
  time: string;
  hours?: number;
  minutes?: number;
  nextDate?: number;
}

export interface OpeningPeriod {
  open: OpeningPoint;
  close?: OpeningPoint;
  hide?: boolean;
}

const CLOSED: DayHours = { kind: 'closed' };
const OPEN24: DayHours = { kind: 'open24' };

const weekOf = (f: (d: Weekday) => DayHours): WeekHours =>
  Object.fromEntries(DAYS.map((d) => [d, f(d)])) as Record<Weekday, DayHours>;

export const CLOSED_WEEK: WeekHours = weekOf(() => CLOSED);

const DAY_MIN = 24 * 60;
const pad = (n: number) => String(n).padStart(2, '0');

/** `HH:MM` → minutes after midnight. */
export const toMinutes = (hhmm: string): number =>
  Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** Minutes after midnight → `HH:MM` (wrapping past midnight). */
export const fromMinutes = (min: number): string => {
  const m = ((min % DAY_MIN) + DAY_MIN) % DAY_MIN;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
};

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export const NOTE_TEXT_LIST = 'Stored as text, not a list — saving rewrites it as a list.';
export const NOTE_UNREADABLE = 'The stored hours could not be read — saving replaces them.';
export const NOTE_SKIPPED = 'Some stored periods could not be read and are left out.';
export const NOTE_NO_CLOSE = 'A stored period has no closing time — shown as open until midnight.';
export const NOTE_SPLIT = 'A stored period runs past a day — shown split by day.';
export const NOTE_OVERLAID = 'A day stored as open all day also had hours — shown as open 24 h.';

export interface ParsedOpeningHours {
  week: WeekHours;
  /** What the week could not show exactly, in words (an info note, never an error). */
  notes: string[];
}

function pointOf(raw: unknown): { day: Weekday; min: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const day = o.day;
  if (typeof day !== 'number' || !Number.isInteger(day) || day < 0 || day > 6) return null;
  if (typeof o.time === 'string' && /^\d{4}$/.test(o.time)) {
    const h = Number(o.time.slice(0, 2));
    const m = Number(o.time.slice(2));
    if (h <= 23 && m <= 59) return { day: day as Weekday, min: h * 60 + m };
  }
  const { hours: h, minutes: m } = o;
  if (
    typeof h === 'number' &&
    typeof m === 'number' &&
    Number.isInteger(h) &&
    Number.isInteger(m) &&
    h >= 0 &&
    h <= 23 &&
    m >= 0 &&
    m <= 59
  ) {
    return { day: day as Weekday, min: h * 60 + m };
  }
  return null;
}

const isAlwaysOpen = (raw: unknown): boolean => {
  if (!raw || typeof raw !== 'object') return false;
  const e = raw as Record<string, unknown>;
  const open = pointOf(e.open);
  return !!open && open.day === 0 && open.min === 0 && (e.close === undefined || e.close === null);
};

/** A stored value → the week, leniently (see the module comment). */
export function parseOpeningHours(raw: unknown): ParsedOpeningHours {
  const notes = new Set<string>();
  let list: unknown = raw;
  if (typeof list === 'string') {
    const text = list.trim();
    if (!text) return { week: CLOSED_WEEK, notes: [] };
    try {
      list = JSON.parse(text);
      notes.add(NOTE_TEXT_LIST);
    } catch {
      return { week: CLOSED_WEEK, notes: [NOTE_UNREADABLE] };
    }
  }
  if (list === null || list === undefined) return { week: CLOSED_WEEK, notes: [] };
  if (!Array.isArray(list)) return { week: CLOSED_WEEK, notes: [NOTE_UNREADABLE] };

  const visible = list.filter(
    (e) => !(e && typeof e === 'object' && (e as { hide?: unknown }).hide === true),
  );
  if (visible.length === 1 && isAlwaysOpen(visible[0])) {
    return { week: weekOf(() => OPEN24), notes: [...notes] };
  }

  const days: Record<Weekday, DayHours> = { ...CLOSED_WEEK };
  const allDay = (d: Weekday) => {
    if (days[d].kind === 'hours') notes.add(NOTE_OVERLAID);
    days[d] = OPEN24;
  };
  const period = (d: Weekday, open: number, close: number) => {
    const day = days[d];
    if (day.kind === 'open24') {
      notes.add(NOTE_OVERLAID);
      return;
    }
    const p = { open: fromMinutes(open), close: fromMinutes(close) };
    days[d] = { kind: 'hours', periods: day.kind === 'hours' ? [...day.periods, p] : [p] };
  };
  const next = (d: Weekday, n = 1) => ((d + n) % 7) as Weekday;

  for (const entry of visible) {
    const e = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    const open = e ? pointOf(e.open) : null;
    if (!e || !open) {
      notes.add(NOTE_SKIPPED);
      continue;
    }
    if (e.close === undefined || e.close === null) {
      notes.add(NOTE_NO_CLOSE);
      if (open.min === 0) allDay(open.day);
      else period(open.day, open.min, 0);
      continue;
    }
    const close = pointOf(e.close);
    if (!close) {
      notes.add(NOTE_SKIPPED);
      continue;
    }
    const gap = (close.day - open.day + 7) % 7;
    // A same-day close at or before the open can only mean the next day.
    const span =
      gap === 0 && close.min <= open.min
        ? DAY_MIN - open.min + close.min
        : gap * DAY_MIN + close.min - open.min;
    // A whole day: 00:00 to the next day's 00:00 (Google), or 00:00–23:59 (the dashboard).
    if (
      open.min === 0 &&
      ((gap === 1 && close.min === 0) || (gap === 0 && close.min === DAY_MIN - 1))
    ) {
      allDay(open.day);
    } else if (span <= DAY_MIN) {
      period(open.day, open.min, close.min);
    } else {
      notes.add(NOTE_SPLIT);
      if (open.min === 0) allDay(open.day);
      else period(open.day, open.min, 0);
      let left = span - (DAY_MIN - open.min);
      let d = next(open.day);
      for (; left >= DAY_MIN; left -= DAY_MIN, d = next(d)) allDay(d);
      if (left > 0) period(d, 0, left);
    }
  }
  return { week: days, notes: [...notes] };
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

const point = (day: Weekday, min: number): OpeningPoint => ({
  day,
  time: `${pad(Math.floor(min / 60))}${pad(min % 60)}`,
  hours: Math.floor(min / 60),
  minutes: min % 60,
});

/** The week → the stored list (see the module comment). A closed week is `[]`: no hours. */
export function toOpeningPeriods(week: WeekHours): OpeningPeriod[] {
  if (DAYS.every((d) => week[d].kind === 'open24')) return [{ open: point(0, 0) }];
  const out: OpeningPeriod[] = [];
  for (const d of DAYS) {
    const day = week[d];
    if (day.kind === 'open24') out.push({ open: point(d, 0), close: point(d, DAY_MIN - 1) });
    if (day.kind !== 'hours') continue;
    for (const p of day.periods) {
      const open = toMinutes(p.open);
      const close = toMinutes(p.close);
      // An equal close stays on the day, so it reads back as the same (flagged) period.
      out.push({
        open: point(d, open),
        close: point(close < open ? (((d + 1) % 7) as Weekday) : d, close),
      });
    }
  }
  return out;
}

export const isClosedWeek = (week: WeekHours): boolean =>
  DAYS.every((d) => week[d].kind === 'closed');

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export const PROBLEM_SAME_TIME = 'Opens and closes at the same time';
export const PROBLEM_OVERLAP = 'Opening times overlap';

/** What is wrong with one day's hours, in words (a save must not pass these). */
export function dayProblems(day: DayHours): string[] {
  if (day.kind !== 'hours') return [];
  const problems: string[] = [];
  if (day.periods.some((p) => p.open === p.close)) problems.push(PROBLEM_SAME_TIME);
  const spans = day.periods
    .filter((p) => p.open !== p.close)
    .map((p) => {
      const open = toMinutes(p.open);
      const close = toMinutes(p.close);
      return [open, close <= open ? close + DAY_MIN : close] as const;
    })
    .sort((a, b) => a[0] - b[0]);
  if (spans.some((s, i) => i > 0 && s[0] < spans[i - 1]![1])) problems.push(PROBLEM_OVERLAP);
  return problems;
}

/** The first problem of a stored value, with its day ("Monday: opening times overlap") —
 *  what a caller blocks its save with; null when none. */
export function openingHoursError(raw: unknown): string | null {
  const { week } = parseOpeningHours(raw);
  for (const d of WEEK_ORDER) {
    const [first] = dayProblems(week[d]);
    if (first) return `${WEEKDAY_NAME[d]}: ${first.charAt(0).toLowerCase()}${first.slice(1)}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** "09:00–17:00", "18:00–02:00 (next day)", "18:00–00:00" (midnight needs no note). */
export function formatPeriod(p: OpenPeriod): string {
  const nextDay = p.close !== '00:00' && toMinutes(p.close) < toMinutes(p.open);
  return `${p.open}–${p.close}${nextDay ? ' (next day)' : ''}`;
}

/** "Closed" / "Open 24 h" / "09:00–13:00, 14:00–18:00". */
export function formatDayHours(day: DayHours): string {
  if (day.kind === 'closed') return DAY_KIND_LABEL.closed;
  if (day.kind === 'open24') return DAY_KIND_LABEL.open24;
  return day.periods.map(formatPeriod).join(', ');
}

/** A stored week in one line — the editor's "was …", the ledger's from → to: runs of days
 *  with the same hours, Monday first, in the rows' own words ("Mon–Fri 09:00–17:00 ·
 *  Sat–Sun Closed"); "Every day …" when all seven agree. */
export function formatWeekHours(raw: unknown): string {
  const { week } = parseOpeningHours(raw);
  const runs: Array<{ from: Weekday; to: Weekday; text: string }> = [];
  for (const d of WEEK_ORDER) {
    const text = formatDayHours(week[d]);
    const last = runs[runs.length - 1];
    if (last && last.text === text) last.to = d;
    else runs.push({ from: d, to: d, text });
  }
  if (runs.length === 1) return `Every day ${runs[0]!.text}`;
  return runs
    .map((r) => `${WEEKDAY_SHORT[r.from]}${r.to === r.from ? '' : `–${WEEKDAY_SHORT[r.to]}`} ${r.text}`)
    .join(' · ');
}

// ---------------------------------------------------------------------------
// Edits (each returns a new week)
// ---------------------------------------------------------------------------

const DEFAULT_PERIOD: OpenPeriod = { open: '09:00', close: '17:00' };

const withDay = (week: WeekHours, d: Weekday, day: DayHours): WeekHours => ({ ...week, [d]: day });

/** Switching a day to "Open" copies the nearest earlier day that has hours (Monday
 *  first), else 09:00–17:00 — entering a week is one choice per day. */
export function setDayKind(week: WeekHours, d: Weekday, kind: DayKind): WeekHours {
  const day = week[d];
  if (day.kind === kind) return week;
  if (kind !== 'hours') return withDay(week, d, kind === 'closed' ? CLOSED : OPEN24);
  const earlier = WEEK_ORDER.slice(0, WEEK_ORDER.indexOf(d))
    .reverse()
    .map((e) => week[e])
    .find((e): e is Extract<DayHours, { kind: 'hours' }> => e.kind === 'hours');
  return withDay(week, d, {
    kind: 'hours',
    periods: (earlier?.periods ?? [DEFAULT_PERIOD]).map((p) => ({ ...p })),
  });
}

/** A further period after the day's last: an hour after it closes, for two hours (or
 *  18:00–22:00 when the day runs late). */
export function addPeriod(week: WeekHours, d: Weekday): WeekHours {
  const day = week[d];
  if (day.kind !== 'hours') return setDayKind(week, d, 'hours');
  const last = day.periods[day.periods.length - 1];
  let added: OpenPeriod = { open: '18:00', close: '22:00' };
  if (last) {
    const open = toMinutes(last.open);
    const close = toMinutes(last.close);
    if (close > open && close + 180 <= DAY_MIN - 1) {
      added = { open: fromMinutes(close + 60), close: fromMinutes(close + 180) };
    }
  }
  return withDay(week, d, { kind: 'hours', periods: [...day.periods, added] });
}

/** Removing a day's last period closes the day. */
export function removePeriod(week: WeekHours, d: Weekday, index: number): WeekHours {
  const day = week[d];
  if (day.kind !== 'hours') return week;
  const periods = day.periods.filter((_, i) => i !== index);
  return withDay(week, d, periods.length ? { kind: 'hours', periods } : CLOSED);
}

/** "Copy to all days": every day takes `d`'s hours (its own copy of the periods) —
 *  Closed and Open 24 h included. */
export function copyDayToAll(week: WeekHours, d: Weekday): WeekHours {
  const day = week[d];
  return weekOf(() => (day.kind === 'hours' ? { kind: 'hours', periods: day.periods.map((p) => ({ ...p })) } : day));
}

/** Whether every day already has `d`'s hours (nothing to copy). */
export function isSameEveryDay(week: WeekHours, d: Weekday): boolean {
  const text = JSON.stringify(week[d]);
  return DAYS.every((o) => JSON.stringify(week[o]) === text);
}

export function setPeriodTime(
  week: WeekHours,
  d: Weekday,
  index: number,
  side: keyof OpenPeriod,
  time: string,
): WeekHours {
  const day = week[d];
  if (day.kind !== 'hours' || !day.periods[index]) return week;
  return withDay(week, d, {
    kind: 'hours',
    periods: day.periods.map((p, i) => (i === index ? { ...p, [side]: time } : p)),
  });
}
